/* ═════════════════════════════════════════════════════════════════════════════
   SRC/COMPONENTS/TABSEGMENTATIONVIEW/DATATRANSFORMS.JS — funções puras de dados
   ─────────────────────────────────────────────────────────────────────────────
   O QUE FAZ
   Transformações de dados (sem React, sem Redux, sem rede) usadas por
   SegmentationView (./index.jsx) para montar os gráficos históricos de
   segmentação. Extraídas do componente pra poderem ser testadas isoladamente
   — mesmo padrão de tabDriversView/dataTransforms.js.

   USADO POR
   ./index.jsx (SegmentationView).
   ═══════════════════════════════════════════════════════════════════════════ */

import { normalizeKey } from "../../utils/normalizeString";

export function normalizeText(value) {
  return normalizeKey(value);
}

const ORDEM_MESES_IDX = {
  Jan: 0,
  Fev: 1,
  Mar: 2,
  Abr: 3,
  Mai: 4,
  Jun: 5,
  Jul: 6,
  Ago: 7,
  Set: 8,
  Out: 9,
  Nov: 10,
  Dez: 11,
};

/**
 * Chave de ordenação cronológica de um nome de período ("Jun/26", "T1/26"
 * etc). `periodMetaByName[periodo].start_date` (via state.dashboard.periods)
 * é a fonte confiável — o parser de "Mês/Ano" abaixo só entende abreviações
 * de mês e cairia tudo no mesmo bucket pra nomes trimestrais/semestrais/
 * anuais, embaralhando a ordem quando esses tipos são selecionados.
 */
export function ordenarPeriodo(periodo, periodMetaByName = {}) {
  const startDate = periodMetaByName[periodo]?.start_date;
  if (startDate) {
    const parsed = new Date(startDate).getTime();
    if (!Number.isNaN(parsed)) return parsed;
  }
  const [mes, ano] = String(periodo || "").split("/");
  return (2000 + Number(ano)) * 12 + (ORDEM_MESES_IDX[mes] ?? 0);
}

export const DESCRIPTION_MAP = {
  // company_type
  local: "loc",
  intercambio: "int",

  empresa: "eGeral",
  individual: "iGeral",

  // general
  geral: "geral",

  // ownership
  titular: "tit",
  dependente: "dep",

  // age
  "gen z": "genZ",
  "gen y": "millennial",
  millennial: "millennial",
  "gen x": "genX",
  boomer: "boomer",
  "baby boomer": "boomer",
  "baby boomers": "boomer",
  "baby bommers": "boomer",
  aposentado: "apo",
};

// customer_time_empresa/customer_time_individual: o prefixo (e/i) vem do
// type_description, e o sufixo da description — combinados dão a key da
// LINE em segSection/index.jsx (ex.: "e5p" = empresa + mais_5_anos).
export const CUSTOMER_TIME_PREFIX_BY_TYPE = {
  customer_time_empresa: "e",
  customer_time_individual: "i",
};
export const CUSTOMER_TIME_SUFFIX_BY_DESCRIPTION = {
  mais_5_anos: "5p",
  de_1_5_anos: "1a5",
  menos_1_ano: "m1",
};

/**
 * Agrupa segmentationHistoryData por mês (filtrado por `category`), uma
 * coluna por segmento (keys de DESCRIPTION_MAP + customer_time_*).
 *
 * Garante todas as keys em todas as linhas: sem isso, um segmento sem dado
 * num mês específico (mas com dado em outro) fica undefined nesse ponto e o
 * Recharts para de desenhar a linha a partir dali em vez de descer a 0 —
 * mesmo bug já corrigido em tabDriversView/dataTransforms.js
 * (buildHistEtapaData).
 */
export function buildSegmentationHistoryData(
  segmentationHistoryData,
  category,
  periodMetaByName = {},
) {
  const grouped = new Map();
  segmentationHistoryData
    .filter((item) => item.category === category)
    .forEach((item) => {
      const fullPeriod = item.period_name;
      const row = grouped.get(fullPeriod) || {
        p: fullPeriod.split("/")[0],
        _ord: ordenarPeriodo(fullPeriod, periodMetaByName),
      };
      const timePrefix =
        CUSTOMER_TIME_PREFIX_BY_TYPE[normalizeText(item.type_description)];
      const timeSuffix =
        CUSTOMER_TIME_SUFFIX_BY_DESCRIPTION[normalizeText(item.description)];
      const key =
        timePrefix && timeSuffix
          ? `${timePrefix}${timeSuffix}`
          : DESCRIPTION_MAP[normalizeText(item.description)];
      if (key) row[key] = item.value;
      grouped.set(fullPeriod, row);
    });

  const rows = Array.from(grouped.values());
  const allKeys = new Set();
  rows.forEach((row) => {
    Object.keys(row).forEach((k) => {
      if (k !== "p" && k !== "_ord") allKeys.add(k);
    });
  });
  rows.forEach((row) => {
    allKeys.forEach((k) => {
      if (row[k] === undefined || Number.isNaN(row[k])) row[k] = 0;
    });
  });

  return rows.sort((a, b) => a._ord - b._ord).map(({ _ord, ...row }) => row);
}
